"use client";

import { useCallback, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Sheet,
  SheetHeader,
  SheetTitle,
  SheetClose,
  SheetBody,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { customInstance } from "@/lib/api/client";
import { showToast } from "@/components/ui/sonner";
import { Icon } from "@/components/ui/icon";
import { Pagination } from "@/components/ui/pagination";
import { CreatePreset } from "./create";
import { FOCUS_RING } from "./icon-button";
import {
  applyStagePresetToStages,
  parseStagePresetItems,
} from "../lib/stage-preset";
import type { StageItem } from "@/features/jobs/components/stage-cards";
import { cn } from "@/lib/utils";

interface StagePresetResponse {
  id: string;
  title: string;
  description?: string;
  stages_json?: string;
  item_count: number;
}

interface StagePresetListResponse {
  presets: StagePresetResponse[];
  total: number;
}

type Tab = "my" | "shared";
const PER_PAGE = 10;

export interface ApplyJobStagePresetProps {
  open: boolean;
  onClose: () => void;
  onApply: (stages: StageItem[]) => void;
  currentStages: StageItem[];
}

export function ApplyJobStagePreset({
  open,
  onClose,
  onApply,
  currentStages,
}: ApplyJobStagePresetProps) {
  const [tab, setTab] = useState<Tab>("my");
  const [page, setPage] = useState(1);
  const [createOpen, setCreateOpen] = useState(false);

  const selector = tab === "my" ? "my" : "shared";

  const { data, isLoading } = useQuery<StagePresetListResponse>({
    queryKey: ["presets", "job_stages", selector, page],
    queryFn: () => {
      const params = new URLSearchParams();
      params.set("preset_type", "job_stages");
      params.set("selector", selector);
      params.set("per_page", String(PER_PAGE));
      params.set("page", String(page));
      return customInstance(`/api/presets?${params.toString()}`);
    },
    enabled: open,
  });

  const presets = data?.presets ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PER_PAGE));

  const handleApply = useCallback(
    (preset: StagePresetResponse) => {
      const items = parseStagePresetItems(preset.stages_json);
      if (!items?.length) {
        showToast.error("This preset has no stages");
        return;
      }
      onApply(applyStagePresetToStages(currentStages, items));
      showToast.success(`Applied "${preset.title}"`);
      onClose();
    },
    [currentStages, onApply, onClose],
  );

  const handleClose = useCallback(() => {
    onClose();
    setPage(1);
    setTab("my");
  }, [onClose]);

  return (
    <>
      <Sheet
        open={open}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) handleClose();
        }}
        size="lg"
        disableFocusTrap
      >
        <SheetHeader>
          <SheetTitle>Apply Job Stage Preset</SheetTitle>
          <SheetClose />
        </SheetHeader>

        <SheetBody className="flex min-h-0 flex-1 flex-col gap-4">
          <Tabs
            value={tab}
            onValueChange={(value) => {
              setTab(value as Tab);
              setPage(1);
            }}
          >
            <TabsList>
              <TabsTrigger value="my">My Presets</TabsTrigger>
              <TabsTrigger value="shared">Shared Presets</TabsTrigger>
            </TabsList>
          </Tabs>

          <div className="min-h-0 flex-1 overflow-y-auto">
            {isLoading ? (
              <p className="text-sm text-contrast/50">Loading presets…</p>
            ) : presets.length === 0 ? (
              <div className="flex flex-col items-center gap-3 py-10 text-center">
                <Icon name="IconPlan" size={24} className="text-heading opacity-30" />
                <p className="text-sm text-contrast/50">
                  No stage presets found. Create one to get started.
                </p>
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={() => setCreateOpen(true)}
                >
                  Create preset
                </Button>
              </div>
            ) : (
              <ul className="flex flex-col gap-2">
                {presets.map((preset) => {
                  const items = parseStagePresetItems(preset.stages_json) ?? [];
                  const stageCount = preset.item_count || items.length;
                  const accessibleName = preset.description
                    ? `Apply preset ${preset.title}. ${preset.description}`
                    : `Apply preset ${preset.title}`;
                  return (
                    <li key={preset.id}>
                      <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        onClick={() => handleApply(preset)}
                        aria-label={accessibleName}
                        className={cn(
                          "h-auto w-full flex-col items-stretch justify-start gap-2 rounded-lg border-border bg-surface-header px-4 py-3 text-left font-normal shadow-none hover:bg-contrast/5 active:bg-contrast/5 [&>[data-slot=button-inset]]:hidden",
                          FOCUS_RING,
                        )}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <p className="truncate text-sm font-medium text-heading">
                              {preset.title}
                            </p>
                            {preset.description ? (
                              <p className="mt-0.5 line-clamp-2 text-xs text-contrast/50">
                                {preset.description}
                              </p>
                            ) : null}
                          </div>
                          <span className="shrink-0 text-xs text-contrast/40">
                            {stageCount} stage{stageCount === 1 ? "" : "s"}
                          </span>
                        </div>
                        {items.length > 0 ? (
                          <div className="flex flex-wrap gap-1.5">
                            {items.map((item) => (
                              <span
                                key={`${preset.id}-${item.name}`}
                                className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-0.5 text-xs text-contrast/70"
                              >
                                <span
                                  aria-hidden="true"
                                  className="size-2 rounded-full"
                                  style={{ backgroundColor: item.color }}
                                />
                                {item.name}
                              </span>
                            ))}
                          </div>
                        ) : null}
                      </Button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          {totalPages > 1 ? (
            <Pagination.Root
              page={page}
              totalPages={totalPages}
              onPageChange={setPage}
              variant="list"
            >
              <Pagination.Nav>
                <Pagination.First />
                <Pagination.Prev />
                <Pagination.Pages
                  strategy="window"
                  maxVisible={4}
                  windowAlign="center"
                />
                <Pagination.Next />
                <Pagination.Last />
              </Pagination.Nav>
            </Pagination.Root>
          ) : null}
        </SheetBody>
      </Sheet>

      <CreatePreset
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        presetType="job-stages"
        initialStages={currentStages}
      />
    </>
  );
}
